import { Box, Button, Flex, FormControl, FormLabel, Input, Select } from '@chakra-ui/react';

import { useGetAvailableUsersAndAllHuntGroupsQuery } from '../../gql/generated/graphql';

export type CallHistoryFilterValues = {
  dateFrom: string;
  dateTo: string;
  agentUserId: string;
  outcome: string;
  flowType: string;
  hasEventType: string;
  callSid: string;
  conferenceId: string;
};

type CallHistoryFiltersProps = {
  filters: CallHistoryFilterValues;
  onChange: (filters: CallHistoryFilterValues) => void;
};

const CallHistoryFilters = ({ filters, onChange }: CallHistoryFiltersProps) => {
  const { data: usersData } = useGetAvailableUsersAndAllHuntGroupsQuery();

  const handleChange = (key: keyof CallHistoryFilterValues, value: string) => {
    onChange({ ...filters, [key]: value });
  };

  const handleReset = () => {
    onChange({
      dateFrom: '',
      dateTo: '',
      agentUserId: '',
      outcome: '',
      flowType: '',
      hasEventType: '',
      callSid: '',
      conferenceId: '',
    });
  };

  return (
    <Flex wrap="wrap" gap={3} alignItems="flex-end" mb={4}>
      <FormControl maxW="160px">
        <FormLabel fontSize="sm" mb={1}>
          From
        </FormLabel>
        <Input
          type="date"
          size="sm"
          value={filters.dateFrom}
          onChange={(e) => handleChange('dateFrom', e.target.value)}
        />
      </FormControl>

      <FormControl maxW="160px">
        <FormLabel fontSize="sm" mb={1}>
          To
        </FormLabel>
        <Input
          type="date"
          size="sm"
          value={filters.dateTo}
          onChange={(e) => handleChange('dateTo', e.target.value)}
        />
      </FormControl>

      <FormControl maxW="200px">
        <FormLabel fontSize="sm" mb={1}>
          Agent
        </FormLabel>
        <Select
          size="sm"
          placeholder="All agents"
          value={filters.agentUserId}
          onChange={(e) => handleChange('agentUserId', e.target.value)}
        >
          {usersData?.getAvailableUsers
            ?.filter((user): user is NonNullable<typeof user> => user != null && user.id != null)
            .map((user) => (
              <option key={user.id} value={user.id ?? undefined}>
                {user.name}
              </option>
            ))}
        </Select>
      </FormControl>

      <FormControl maxW="160px">
        <FormLabel fontSize="sm" mb={1}>
          Outcome
        </FormLabel>
        <Select
          size="sm"
          placeholder="All outcomes"
          value={filters.outcome}
          onChange={(e) => handleChange('outcome', e.target.value)}
        >
          <option value="answered">Answered</option>
          <option value="voicemail">Voicemail</option>
          <option value="noanswer">No Answer</option>
          <option value="hungup">Hung Up</option>
        </Select>
      </FormControl>

      <FormControl maxW="160px">
        <FormLabel fontSize="sm" mb={1}>
          Flow Type
        </FormLabel>
        <Select
          size="sm"
          placeholder="All flows"
          value={filters.flowType}
          onChange={(e) => handleChange('flowType', e.target.value)}
        >
          <option value="huntgroup">Hunt Group</option>
          <option value="sip">SIP</option>
          <option value="clicktocall">Click to Call</option>
          <option value="inbound_other">Inbound Other</option>
          <option value="outbound_other">Outbound Other</option>
          <option value="Thunder">Thunder</option>
        </Select>
      </FormControl>

      <FormControl maxW="200px">
        <FormLabel fontSize="sm" mb={1}>
          Event Filter
        </FormLabel>
        <Select
          size="sm"
          placeholder="No event filter"
          value={filters.hasEventType}
          onChange={(e) => handleChange('hasEventType', e.target.value)}
        >
          <option value="sip_failure">Has SIP Failure</option>
          <option value="transfer_initiated">Was Transferred</option>
          <option value="voicemail_sent">Went to Voicemail</option>
        </Select>
      </FormControl>

      <FormControl maxW="220px">
        <FormLabel fontSize="sm" mb={1}>
          Call SID
        </FormLabel>
        <Input
          size="sm"
          placeholder="CA..."
          value={filters.callSid}
          onChange={(e) => handleChange('callSid', e.target.value)}
        />
      </FormControl>

      <FormControl maxW="220px">
        <FormLabel fontSize="sm" mb={1}>
          Conference ID
        </FormLabel>
        <Input
          size="sm"
          placeholder="CF..."
          value={filters.conferenceId}
          onChange={(e) => handleChange('conferenceId', e.target.value)}
        />
      </FormControl>

      <Box>
        <Button size="sm" variant="outline" onClick={handleReset}>
          Reset
        </Button>
      </Box>
    </Flex>
  );
};

export default CallHistoryFilters;
